import Component from "@glimmer/component";
import { getOwnerWithFallback } from "discourse/lib/get-owner";
import { withPluginApi } from "discourse/lib/plugin-api";
import { formatUsername } from "discourse/lib/utilities";
import Category from "discourse/models/category";
import { i18n } from "discourse-i18n";
import CirclesAddCirclesCategoryBodyClass from "../components/circles/add-circles-category-body-class";
import CirclesMembershipPostRequest from "../components/circles/membership-post-request";
import CirclesUserNavCirclesItem from "../components/circles/user-nav-circles-item";
import CirclesUserNotificationPreferences from "../components/circles/user-notification-preferences";

const PLUGIN_ID = "discourse-circles";

export default {
  name: PLUGIN_ID,

  initialize(container) {
    const circles = container.lookup("service:circles");

    if (circles.userCanUseCircles) {
      withPluginApi((api) => {
        addNavigationBarItem(api);
        addCircleMembershipRequestPostDecorator(api);
        addCustomUserSearchOptions(api);
        enhanceTopicListWithCircles(api);
        redirectCategories(api);
        addPostSmallActionIcons(api);
        addNotificationOnCircleMentionUserField(api);
        replaceIcons(api);
        registerCirclesActivityNotificationType(api);
        addCirclesCategoryBodyClass(api);
        mountUserNavCirclesItem(api);
        mountUserNotificationsPreferences(api);
      });
    }
  },
};

function addNavigationBarItem(api) {
  api.addNavigationBarItem({
    name: "circles",
    displayName: i18n("discourse_circles.index.title"),
    title: i18n("discourse_circles.index.title"),
    href: "/circles",
  });
}

function addCircleMembershipRequestPostDecorator(api) {
  api.renderAfterWrapperOutlet(
    "post-content-cooked-html",
    class CirclesMembershipConnector extends Component {
      static shouldRender(args) {
        return (
          args.post?.post_number === 1 &&
          args.post?.topic?.requested_circle_membership_details
        );
      }

      get details() {
        return this.args.post.topic.requested_circle_membership_details;
      }

      <template>
        <CirclesMembershipPostRequest
          @post={{@post}}
          @circleCategoryId={{this.details.circle_category_id}}
          @requestId={{this.details.circle_request_id}}
          @userId={{this.details.requested_circle_user_id}}
        />
      </template>
    }
  );
}

function addCustomUserSearchOptions(api) {
  api.addUserSearchOption("groupIds");
}

function enhanceTopicListWithCircles(api) {
  api.registerValueTransformer(
    "topic-list-topics-from",
    ({ value: topics, context }) => {
      const circles = context.result?.topic_list?.circles;

      if (topics && circles) {
        const circlesService = getOwnerWithFallback(context.store).lookup(
          "service:circles"
        );

        // add the retrieved circles to the service cache
        circlesService.storeCircles(circles);

        topics.forEach((topic) => {
          if (topic.category_id) {
            const topicCircle = circlesService.findCached(topic.category_id);

            if (topicCircle) {
              topic.circle = topicCircle;
            }
          }
        });
      }

      return topics;
    }
  );
}

function redirectCategories(api) {
  api.modifyClass(`route:discovery.category`, {
    pluginId: PLUGIN_ID,

    async beforeModel(transition) {
      const category_slug_path_with_id =
        transition?.to?.params?.category_slug_path_with_id;

      if (!category_slug_path_with_id) {
        return this._super(...arguments);
      }

      // tests the existence of a category matching `category_slug_path_with_id`
      // cached.
      const category = Category.findBySlugPathWithID(
        category_slug_path_with_id
      );

      if (category) {
        if (category.is_circle) {
          // redirect in case it is a circle
          this.router.replaceWith("circle", category_slug_path_with_id);
        } else {
          return this._super(...arguments);
        }
      }

      // if a category was not found we need to search for a circle matching
      // the category_slug_path_with_id provided
      try {
        const circle = await this.store.find(
          "circle",
          category_slug_path_with_id
        );

        // if a circle matching `category_slug_path_with_id` was found
        // redirect to the circle
        if (circle) {
          // TODO cache this result to avoid another call to the end-point
          this.router.replaceWith("circle", category_slug_path_with_id);
        } else {
          return this._super(...arguments);
        }
      } catch (e) {
        if (e.jqXHR?.status === 404 || e.status === 404) {
          // if the circle was a 404 let the transition proceed
          return this._super(...arguments);
        } else {
          throw e;
        }
      }
    },
  });
}

function addPostSmallActionIcons(api) {
  api.addPostSmallActionIcon(
    "circle_membership_request_accepted",
    "circle-dot"
  );
  api.addPostSmallActionIcon("circle_membership_request_denied", "circle-dot");
  api.addPostSmallActionIcon(
    "circle_membership_request_cancelled",
    "circle-dot"
  );
}

function replaceIcons(api) {
  api.replaceIcon("discourse_circles", "circle-dot");
  api.replaceIcon("discourse_circles_public_circle", "globe");
  api.replaceIcon("discourse_circles_private_circle", "lock");
  api.replaceIcon("notification.circles_activity", "circle-dot");
  api.replaceIcon(
    "notification.circles_activity_request_membership_pm",
    "circle-dot"
  );
}

function registerCirclesActivityNotificationType(api) {
  api.registerNotificationTypeRenderer(
    "circles_activity",
    (NotificationTypeBase) => {
      return class extends NotificationTypeBase {
        get label() {
          const data = this.notification.data;
          const { circle_activity_type: type } = data;

          switch (type) {
            case "request_membership_consolidated":
              return i18n(`discourse_circles.notifications.labels.${type}`, {
                count: data.count,
              });
            default:
              return formatUsername(data.username);
          }
        }

        get description() {
          const data = this.notification.data;
          const { circle_activity_type: type } = data;

          return i18n(`discourse_circles.notifications.descriptions.${type}`, {
            circle_name: data.circle_name,
          });
        }

        get linkTitle() {
          const data = this.notification.data;
          const { circle_activity_type: type } = data;
          return i18n(`discourse_circles.notifications.titles.${type}`);
        }

        get linkHref() {
          const data = this.notification.data;
          const { circle_activity_type: type } = data;

          switch (type) {
            case "request_membership":
            case "request_membership_consolidated":
              return `/circles/${data.circle_category_id}/requests`;
            default:
              return `/circles/${data.circle_category_id}`;
          }
        }
      };
    }
  );
}

function addNotificationOnCircleMentionUserField(api) {
  api.addSaveableCustomFields("notifications");
  api.addSaveableUserOption("notify_on_circle_mention", {
    page: "notifications",
  });
}

function addCirclesCategoryBodyClass(api) {
  api.renderInOutlet(
    "above-category-heading",
    CirclesAddCirclesCategoryBodyClass
  );
  api.renderInOutlet(
    "topic-above-post-stream",
    CirclesAddCirclesCategoryBodyClass
  );
}

function mountUserNavCirclesItem(api) {
  api.renderInOutlet("user-main-nav", CirclesUserNavCirclesItem);
}

function mountUserNotificationsPreferences(api) {
  api.renderInOutlet(
    "user-preferences-notifications",
    CirclesUserNotificationPreferences
  );
}
